import { Account,  UserType, AccountCode, MFDvreType, AccountType, OrderType, TransactionType, OrderDuration, DeskRouteCode, CommType, AcctClassificationCdArray } from '../models/common/trading-common.model';
import { TradingEntitlements } from '../models/common/trading-entitlements';
import { FormBuilder, FormGroup, AbstractControl, Validators, FormControl, FormArray } from '@angular/forms';
import { BuyAtNavIndicator } from '../models/mutualfund/trading-mutualfund.model';

export function GetUserType(account: Account, entitlements: TradingEntitlements): UserType {

    if (!entitlements.isRiaUser) {
        if (!entitlements.isBdUser) {
            // User is neither RIA nor BD, most likely a retail user
            return null;
        } else {
            return UserType.BD;
        }
    } else {
        // Check if user is DUAL role before returning RIA type
        if (!entitlements.isBdUser) {
            return UserType.RIA;
        } else {
            if (!account) {
                // We can't make a determination, so return DUAL type
                return UserType.DUAL;
            }
            return GetUserTypeFromAccount(account, entitlements.isRiaUser, entitlements.isBdUser);
        }
    }
}

export function GetUserTypeFromAccount(account: Account, riaUser: boolean, bdUser: boolean): UserType {
    // Check the access category code for the account when user is DUAL role,
    // and make a final determination of role based on the code
    let acctCatg = account.acctAccessCatgCd;

    if (AccountCode.ADVI_ACCOUNT_CD === acctCatg) {
        return UserType.RIA;
    } else if (AccountCode.BROKER_ACCOUNT_CD === acctCatg) {
        return UserType.BD;
    } else {
        // Account access category code is neither advisory nor professional.
        // We're only calling this function in response to determining the user
        // is DUAL role, so just return DUAL
		// Returning as BD role to sync with .NET logic
        return UserType.BD;
    }
}

export function  handleHourAsNumber(hourAsNumber: number): [string, number] {
    if (hourAsNumber >= 12) {
        if ( hourAsNumber > 12 ) {
            hourAsNumber -= 12;
        }
        return ['PM', hourAsNumber];
    } else {
        // Adjust midnight hour (0) to 12 so we correctly show 12:## AM
        if ( hourAsNumber === 0 ) {
            hourAsNumber = 12;
        }
        return ['AM', hourAsNumber];
    }
}

export function GetFundLoadTypeByIndication(indicator: string): string {
    let ind = (indicator || '').trim();
    if (ind === 'N' || ind === 'X') {
        return 'No Load Fund';
    }

    if (ind === 'L') {
        return 'Front-End Load';
    }

    if (ind === 'B') {
        return 'Back-End Load';
    }

    if (ind === 'D') {
        return 'Dual-Load';
    }

    return '';
}

export function getDialogWidth(windowSize: number): string {
    let dialogWidth = '80%';
    if (windowSize > 1479) {
        dialogWidth = '60%';
    } else if (windowSize > 1199 && windowSize < 1480) {
        dialogWidth = '70%';
    } else if (windowSize > 1023 && windowSize < 1200) {
        dialogWidth = '75%';
    } else if (windowSize > 767 && windowSize < 1024) {
        dialogWidth = '80%';
    } else if (windowSize < 768) {
        dialogWidth = '85%';
    }

    return dialogWidth;
}

export function createTradingSettingsForm(fb: FormBuilder): FormGroup {
    return fb.group({
        addOrdInfAlwaysDisplay: null,
        addOrdInfAlwaysDisplayEditable: null,
        commissionAlwaysDisplay: null,
        commissionAlwaysDisplayEditable: null,
        regInfAlwaysDisplay: null,
        regInfAlwaysDisplayEditable: null,
        accountType: null,
        accountTypeEditable: null,
        orderType: null,
        orderTypeEditable: null,
        trailingBasedOn: null,
        trailingBasedOnEditable: null,
        duration: null,
        durationEditable: null,
        useAveragePrice: null,
        useAveragePriceEditable: null,
        solicited: null,
        solicitedEditable: null,
        discretion: null,
        discretionEditable: null,
        sourceOfInput: null,
        sourceOfInputEditable: null,
        commissionType: null,
        commissionTypeEditable: null,
        ibdMemo: null,
        ibdMemoEditable: null,
        deskRoute1Code: null,
        deskRoute1CodeEditable: null,
        deskRoute2Code: null,
        deskRoute2CodeEditable: null,
        reinvest: null,
        reinvestEditable: null,
        buyAtNav: null,
        buyAtNavEditable: null
    });
}

export function createOrderProspectusForm(fb: FormBuilder): FormGroup {
  return fb.group({
      firstNme: ['', [Validators.required]],
      lastNme: ['', [Validators.required]],
      addrTxt1: ['', [Validators.required]],
      addrTxt2: [''],
      cityNme: ['', [Validators.required]],
      stateCd: ['', Validators.required],
      zipCd: ['', [Validators.required, Validators.pattern('[0-9]{5}')]],       // Validators.pattern('[0-9]{5}') - US Zip code pattern
      dayPhnNum: ['', [Validators.required, Validators.pattern('^\\(?([0-9]{3})\\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$')]],
      evePhnNum: ['', [Validators.required, Validators.pattern('^\\(?([0-9]{3})\\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$')]]
  });
}

export function getDefaultSolicitedFromTradingDefaults(tradingDefaults: any[]) {
    let defaultVal: boolean;
    (tradingDefaults || []).forEach((item: any) => {
        let settingCode = item.settingCode;
        if (settingCode === 'SOLIC') {
            if (item.settingValue === 'Y') {
                defaultVal = true;
            }
            if (item.settingValue === 'N') {
                defaultVal = false;
            }
        }
    });
    return defaultVal;
}

export function getDefaultNavReasonFromTradingDefaults(tradingDefaults: any[]) {
    let defaultVal: any;
    (tradingDefaults || []).forEach((item: any) => {
        let settingCode = item.settingCode;
        if (settingCode === 'BYNAV') {
            switch (item.settingValue) {
                case 'N': {
                   defaultVal = BuyAtNavIndicator.NO;
                   break;
                }
                case '0': {
                    defaultVal = BuyAtNavIndicator.YES_OTHERS;
                    break;
                }
                case '1': {
                    defaultVal = BuyAtNavIndicator.YES_NAV_TRANSFER;
                    break;
                }
                case '2': {
                    defaultVal = BuyAtNavIndicator.YES_REPURCHASES;
                    break;
                }
                case '3': {
                    defaultVal = BuyAtNavIndicator.YES_EMPLOYEE_REP;
                    break;
                }
                case '4': {
                    defaultVal = BuyAtNavIndicator.YES_ERROR_CORRECTION;
                    break;
                }
                case '5': {
                    defaultVal = BuyAtNavIndicator.YES_WRAP_FEE_ACCT;
                    break;
                }
                case '6': {
                    defaultVal = BuyAtNavIndicator.YES_NAV_COMMISSIONABLE;
                    break;
                }
                case '7': {
                    defaultVal = BuyAtNavIndicator.YES_COMM_DEFINED_CONTRIBUTION_PLAN;
                    break;
                }
                case '8': {
                    defaultVal = BuyAtNavIndicator.YES_NON_COMM_DEFINED_CONTRIBUTION_PLAN;
                    break;
                }
                case 'A': {
                    defaultVal = BuyAtNavIndicator.YES_DIVIDEND_REINVESTMENT_TRADE;
                    break;
                }
                case 'B': {
                    defaultVal = BuyAtNavIndicator.YES_TRUST_ACCT;
                    break;
                }
                case 'E': {
                    defaultVal = BuyAtNavIndicator.YES_SELF_DIRECTED;
                    break;
                }
                default: {
                    defaultVal = '';
                    break;
                }
            }
        }
    });
    return defaultVal;
}

export function getMFDvreTypeFromTradingDefaults(tradingDefaults: any[]) {
    let reinvestCapital: string;
    let reinvestDiv: string;
    let dvreCode: MFDvreType;
    (tradingDefaults || []).forEach((item: any) => {
        let settingCode = item.settingCode;
        if (settingCode === 'RCAPG') {
            reinvestCapital = item.settingValue;
        }
        if (settingCode === 'RDINT') {
            reinvestDiv = item.settingValue;
        }
    });

    if (reinvestCapital === 'R' && reinvestDiv === 'R') {
        dvreCode = MFDvreType.REINVEST;
    }

    if (reinvestCapital === 'R' && reinvestDiv === 'C') {
        dvreCode = MFDvreType.CAPITAL;
    }

    if (reinvestCapital === 'C' && reinvestDiv === 'R') {
        dvreCode = MFDvreType.DIVIDEND;
    }

    if (reinvestCapital === 'C' && reinvestDiv === 'C') {
        dvreCode = MFDvreType.CASH;
    }

    return dvreCode;
}

export function getDefaultDiscFromTradingDefaults(tradingDefaults: any[]) {
    let defaultVal = '';
    (tradingDefaults || []).forEach((item: any) => {
        let settingCode = item.settingCode;
        if (settingCode === 'DISCR') {
            defaultVal = item.settingValue;
        }
    });
    return defaultVal;
}

export function getDefaultIBDMemoFromTradingDefaults(tradingDefaults: any[]) {
    let defaultVal = '';
    (tradingDefaults || []).forEach((item: any) => {
        let settingCode = item.settingCode;
        if (settingCode === 'IBDMO') {
            defaultVal = item.settingValue || '';
            if (defaultVal === 'ADDL') {  // for equity empty Memo content, the ':' is missing - Only 'ADDL'
              defaultVal = '';
            }
            if (defaultVal === 'DIM') {  // for options empty Memo content, the ':' is missing - Only 'DIM'
              defaultVal = '';
            }

            if (defaultVal.lastIndexOf('ADDL:', 0) === 0) {   // for equity
              defaultVal = defaultVal.substring(5).replace(/>$/, '');
            } else if (defaultVal.lastIndexOf('DIM:', 0) === 0) {    // for option
              defaultVal = defaultVal.substring(4).replace(/>$/, '');
            }
        }
    });
    return defaultVal;
}

export function getDefaultSourceOverrideFromTradingDefaults(tradingDefaults: any[]) {
    let defaultVal;
    (tradingDefaults || []).forEach((item: any) => {
        let settingCode = item.settingCode;
        if (settingCode === 'INPUT') {
            defaultVal = item.settingValue;
        }
    });
    return defaultVal;
}

export function getDefaultTrailingBasedOnFromTradingDefaults(tradingDefaults: any[]) {
    let defaultVal = '';
    (tradingDefaults || []).forEach((item: any) => {
        let settingCode = item.settingCode;
        if (settingCode === 'TBASE') {
            if (item.settingValue === 'LTP') {
                defaultVal = 'B';
            }
            if (item.settingValue === 'LQP') {
                defaultVal = 'A';
            }
        }
    });
    return defaultVal;
}

export function getDefaultAccountTypeFromTradingDefaults(tradingDefaults: any[]) {
    let defaultVal = '';
    (tradingDefaults || []).forEach((item: any) => {
        let settingCode = item.settingCode;
        if (settingCode === 'ACTYP') {
            if (item.settingValue === '1') {
                defaultVal = AccountType.CASH;
            }
            if (item.settingValue === '2') {
                defaultVal = AccountType.MARGIN;
            }
        }
    });
    return defaultVal;
}

export function getDefaultPAIndicatorFromTradingDefaults(tradingDefaults: any[]) {
    let defaultVal = '';
    (tradingDefaults || []).forEach((item: any) => {
        let settingCode = item.settingCode;
        if (settingCode === 'PAIND') {
            // value 'A' | 'P' | 'R'
            defaultVal = item.settingValue;
        }
    });
    return defaultVal;
}

export function getDefaultTradeUseAveragePriceFromTradingDefaults(tradingDefaults: any[]) {
    let defaultVal: boolean;
    (tradingDefaults || []).forEach((item: any) => {
        let settingCode = item.settingCode;
        if (settingCode === 'TUAPR') {
            if (item.settingValue === 'Y') {
                defaultVal = true;
            }
            if (item.settingValue === 'N') {
                defaultVal = false;
            }
        }
    });
    return defaultVal;
}

export function getDefaultCustFirmIndFromTradingDefaults(tradingDefaults: any[]) {
  let defaultVal: string;
  (tradingDefaults || []).forEach((item: any) => {
      let settingCode = item.settingCode;
      if (settingCode === 'CFIND') {
          if (item.settingValue === 'C') {
              defaultVal = 'C';
          }
          if (item.settingValue === 'B') {
              defaultVal = 'B';
          }
      }
  });
  return defaultVal;
}

export function getCustFirmIndByBizPurposeCode(code: string, mapping) {
  if (code && mapping[code.toUpperCase()]) {
    return mapping[code];
  } else {
    return 'C';
  }
}

export function getDefaultOrderTypeFromTradingDefaults(tradingDefaults: any[]) {
    let defaultVal: any;
    (tradingDefaults || []).forEach((item: any) => {
        let settingCode = item.settingCode;
        if (settingCode === 'ORDTY') {
            switch (item.settingValue) {
                case '1': {
                   defaultVal = OrderType.MARKET;
                   break;
                }
                case '2': {
                    defaultVal = OrderType.LIMIT;
                    break;
                }
                case '3': {
                    defaultVal = OrderType.STOP;
                    break;
                }
                case '3SL': {
                    defaultVal = OrderType.STOP_LIMIT;
                    break;
                }
                case 'C': {
                    defaultVal = OrderType.EXECUTE_AT_CLOSE;
                    break;
                }
                case '2LOC': {
                    defaultVal = OrderType.LIMIT_ON_CLOSE;
                    break;
                }
                case 'O': {
                    defaultVal = OrderType.OR_BETTER;
                    break;
                }
                case 'W': {
                    defaultVal = OrderType.WOW;
                    break;
                }
                default: {
                    defaultVal = '';
                    break;
                }
            }
        }
    });
    return defaultVal;
}

export function getDefaultDurationsFromTradingDefaults(tradingDefaults: any[]) {
    let defaultVal;
    (tradingDefaults || []).forEach((item: any) => {
        let settingCode = item.settingCode;
        if (settingCode === 'DURTN') {
            switch (item.settingValue) {
                case '1': {
                   defaultVal = OrderDuration.DAY;
                   break;
                }
                case '2': {
                    defaultVal = OrderDuration.GTC;
                    break;
                }
                case '3': {
                    defaultVal = OrderDuration.OPG;
                    break;
                }
                case '4': {
                    defaultVal = OrderDuration.IOC;
                    break;
                }
                case '5': {
                    defaultVal = OrderDuration.FOK;
                    break;
                }
                case '7': {
                    defaultVal = OrderDuration.GTD;
                    break;
                }
                case '8': {
                    defaultVal = OrderDuration.GTM;
                    break;
                }
                default: {
                    defaultVal = '';
                    break;
                }
            }
        }
    });
    return defaultVal;
}

export function getDefaultCommissionTypeFromTradingDefaults(tradingDefaults: any[]) {
    let defaultVal = '';
    (tradingDefaults || []).forEach((item: any) => {
        let settingCode = item.settingCode;
        if (settingCode === 'COMTY') {
            switch (item.settingValue) {
                case 'RTE': {
                   defaultVal = CommType.RTE;
                   break;
                }
                case 'MLT': {
                    defaultVal = CommType.MLT;
                    break;
                }
                case 'COM': {
                    defaultVal = CommType.COM;
                    break;
                }
                case 'PFM': {
                    defaultVal = CommType.PFM;
                    break;
                }
                case 'MUD': {
                    defaultVal = CommType.MUD;
                    break;
                }
                case 'MUD-AUTO': {
                    defaultVal = CommType.MUD_AUTO;
                    break;
                }
                default: {
                    defaultVal = '';
                    break;
                }
            }
        }
    });
    return defaultVal;
}

// deskCodeNum should be 1 or 2 ... to identify 'DESK1', 'DESK2' ...
export function getDefaultDeskCodeFromTradingDefaults(tradingDefaults: any[], deskCodeNum: any) {
    let defaultVal: any;
    let fieldCode = 'DESK' + deskCodeNum;
    (tradingDefaults || []).forEach((item: any) => {
        let settingCode = item.settingCode;
        if (settingCode === fieldCode) {
            switch (item.settingValue) {
                case 'A': {
                   defaultVal = DeskRouteCode.A;
                   break;
                }
                case 'AR': {
                    defaultVal = DeskRouteCode.AR;
                    break;
                }
                case 'B': {
                    defaultVal = DeskRouteCode.B;
                    break;
                }
                case 'C': {
                    defaultVal = DeskRouteCode.C;
                    break;
                }
                case 'D': {
                    defaultVal = DeskRouteCode.D;
                    break;
                }
                case 'IS': {
                    defaultVal = DeskRouteCode.IS;
                    break;
                }
                case 'IN': {
                    defaultVal = DeskRouteCode.IN;
                    break;
                }
                case 'O': {
                    defaultVal = DeskRouteCode.O;
                    break;
                }
                case 'PF': {
                    defaultVal = DeskRouteCode.PF;
                    break;
                }
                case 'PT': {
                    defaultVal = DeskRouteCode.PT;
                    break;
                }
                case 'PR': {
                    defaultVal = DeskRouteCode.PR;
                    break;
                }
                case 'S': {
                    defaultVal = DeskRouteCode.S;
                    break;
                }
                case 'T': {
                    defaultVal = DeskRouteCode.T;
                    break;
                }
                case 'TR': {
                    defaultVal = DeskRouteCode.TR;
                    break;
                }
                case 'SW': {
                    defaultVal = DeskRouteCode.SW;
                    break;
                }
                case 'EC': {
                    defaultVal = DeskRouteCode.EC;
                    break;
                }
                case 'CR': {
                    defaultVal = DeskRouteCode.CR;
                    break;
                }
                default: {
                    defaultVal = '';
                    break;
                }
            }
        }
    });
    return defaultVal;
}

export function getPaIndByBizPurposeCode(code: string, mapping) {
  if (code && mapping[code.toUpperCase()]) {
    return mapping[code];
  } else {
    return 'A';
  }
}

function validate(account: Account): string {
        console.log('acctStatus : '+account.acctStatus
                    +'deactivationDate : '+account.deactivationDate
                    +'pendingCloseInd : '+account.pendingCloseInd
                    +'acctCatgCd : '+account.acctCatgCd
                    +'errorCheckingLevel4Plan : '+account.errorCheckingLevel4Plan
                    +'planLevelCode : '+account.planLevelCode);
        // following *if-test* order matters - only matching case message will display to user
        if (!!account.acctStatus && (account.acctStatus.trim().toUpperCase() === 'I' )) {
            return 'invalid';
        }
        if ('deactivationDate' in account && !!account.deactivationDate && !!account.acctStatus && account.acctStatus.trim().toUpperCase() === 'D') {
            return 'deactivated';
        }
        if (!!account.pendingCloseInd && account.pendingCloseInd.toUpperCase() === 'C') {
            return 'closed';
        }
        if (!!account.pendingCloseInd && account.pendingCloseInd.toUpperCase() === 'P') {
            return 'pending';
        }
        if (!!account.acctCatgCd && account.acctCatgCd.indexOf('529') > -1) {
            if (account.errorCheckingLevel4Plan) {
                return '';
            } else if (account.planLevelCode !== '-1' && account.planLevelCode !== '3' && account.planLevelCode !== '4') {
                return 'invalidPlanLevel';
            } else {
                return '529';
            }
        }

    return '';
}

export function validateAccount(account: Account, tradeType?: string): string {
    let acc;
    if (!!account && !!account.acctNum) {
        acc = validate(account);
        // Disallow option trading when either property is set to "0"
        if (
            tradeType === 'OPTION' && ((!!account.optionAuthInd && account.optionAuthInd.trim() === '0') ||
            (!!account.optionFunctionCode && account.optionFunctionCode.trim() === '0'))
         ) {
            acc = 'no_options_trading';
        }
    }
    return acc;
}


export function backToDotCOM(info: any) {
  document.dispatchEvent(new CustomEvent('portal.gotoview', {
    bubbles: true,
    detail: {
      domain: info.source,
      value: info.account,
      gotoPage: info.gotoPage,
      gotoParams: {
        isUserFromLegacyOTS: true,
        useraction: info.useraction
      }
    }
  }));
}

export function createQtyCalculatorForm(fb: FormBuilder): FormGroup {
    return fb.group({
        amount: ['', [Validators.pattern(/^[0-9]+(\.[0-9]{1,2})?$/), calcInputValidator]],
        price: ['', [Validators.pattern(/^[0-9]+(\.[0-9]{1,2})?$/), calcInputValidator]]
    });
}

export function calcInputValidator(control: AbstractControl) {

    if (control.pristine) {
        return null;
    }

    if (control.value === null || control.value === undefined) {
        return {PRICE_IS_REQUIRED: true};
    } else if (control.value <= 0) {
        return {PRICE_CANNOT_BE_0: true};
     } else if (control.value > 999999999.99) {
        return {PRICE_CANNOT_BE_MORE_THAN_999999999: true};
    } else {
        return null;
    }

}

export function isAveragePriceAccount(acctNum: string): boolean {
    let isApa = false;
    if (!!acctNum && acctNum.length > 0 &&
        (acctNum.slice(3, 6) === '896' || acctNum.slice(3, 6) === '897' || acctNum.slice(0, 3) === '950')) {
        isApa = true;
    }

    return isApa;
}

export function setShowCatEntity(isAvgPriceAcct: boolean, acctClassificationCd: string, isEntitledtToShowCatEntity: boolean, showCatEntity: boolean): boolean {
    if ( !isAvgPriceAcct || !acctClassificationCd ) {
        return false;
    }

    let i: number;
    for (i = 0; i < AcctClassificationCdArray.length && !showCatEntity; i++) {
        if (AcctClassificationCdArray[i] === acctClassificationCd && isEntitledtToShowCatEntity) {
            return true;
        }
    }
    return false;
}

export function isOneOnOneAveragePriceAccount(acctNum: string, entitlement: TradingEntitlements): boolean {
  return entitlement?.averagePriceAccountInd === 'A' && isAveragePriceAccount(acctNum);
}

// codeName :
// ADINF: additionalOrderInformationAlwaysDisplay
// COMMI: commissionIpOverrrideAlwaysDisplay
// REGUL: regulatoryInformationAlwaysDisplay
export function getAlwaysDisplayFromTradingDefaults(tradingDefaults: any[], codeName: string): boolean {
    let defaultVal: boolean = false;
    if (!!tradingDefaults) {
        (tradingDefaults || []).forEach((item: any) => {
            let settingCode = item.settingCode;
            if (settingCode === codeName) {
                if (item.settingValue === 'Y') {
                    defaultVal = true;
                } else if (item.settingValue === 'N') {
                    defaultVal = false;
                }
            }
        });
    }
    return defaultVal;
}

export function validateAllFormControls(orderForm: FormGroup | FormArray) {
  // Some fields has extra validation logic more than formControl validators
  // For example: symId has validation logic based on equity/mutual fund details call
  // For this type of fields, trigger validation will remove extra validation errors, which should NOT happen
  const hasExtraValidationFieldsList = ['symId', 'exchgToSymId', 'secIbdOrdRcvDateTime', 'secIbdOrdRcvDate', 'secIbdOrdRcvTime', 'secIbdOrdRcvTimeZone', 'oatsDeskReceiptDateTime1',
                                        'oatsDeskReceiptDate1', 'oatsDeskReceiptTime1', 'oatsDeskReceiptDate2', 'oatsDeskReceiptTime2', 'expirationDate', 'strikePrice', 'chainType',
                                        'oatsDeskReceiptDateTime2', 'oatsDeskReceiptZone1', 'oatsDeskReceiptZone2', 'limitPrice', 'stopPrice'];

  Object.keys(orderForm.controls).forEach(field => {
    const control = orderForm.get(field);
    if (control instanceof FormControl) {
      if(control.status === 'INVALID') {
        console.log('==== invalid field caught: ', field);  // TODO: remove
        if (hasExtraValidationFieldsList.indexOf(field) > -1 && control.dirty) {
            return;
        }

        if ( control.errors.QTY_CANNOT_BE_MORE_THAN_POSITION_CONTRACT_LIMIT ) {
            // workaround after moving Options contracts Qty to first line
            // return here - keeps the QTY_CANNOT_BE_MORE_THAN_POSITION_CONTRACT_LIMIT, otherwise updateValueAndValidity() will remove this error
            return;
        }

        control.markAsDirty({ onlySelf: true });
        control.markAsTouched({ onlySelf: true });
        control.updateValueAndValidity({ onlySelf: true });
      }
    } else if (control instanceof FormGroup || control instanceof FormArray) {
      if (control.validator) {                                // if there is Form level validator
        control.updateValueAndValidity({ onlySelf: true });   // trigger Form level validator
      }
      validateAllFormControls(control);
    }
  });
}

export function constructUrlParams(urlParams: any) {
    let params = '';
    for (const key in urlParams) {
        params = params + `${key}=${urlParams[key]}&`;
    }
    if(params.length > 0 && params.endsWith('&')){
        params = params.slice(0, -1);
    }
    return params;
}

// truncate to 1 - 9 decimals
// truncDecimals(19.99999999, 4)  =>  19.9999
// truncDecimals(19.99999999, 0)  =>  19
// truncDecimals(19.99999999, 20)  =>  19.99999999
export function truncDecimals(figure: number, decimals: number): number {
  // decimals should be 0 - 9 - fix compatibility issue cross browsers
  if ( decimals < 0 || decimals >= 10 ) {
    return figure;
  }

  let x = decimals - 11;
  return Number(figure.toFixed(11).slice(0, x));
};
export function autoFillEvent(inputvalue:any){
    let input=inputvalue;
    if(!!input){
        input = input.replace(/\D/g,'');
        if(input.length===6){
            input = input.substring(0,2)+':'+input.substring(2,4)+':'+input.substring(4,6);
        } else if(input.length===1 && /[1-9]/.test(input)) {
            input = '0' + input + '0000';
            input = input.substring(0,2)+':'+input.substring(2,4)+':'+input.substring(4,6);
        } else if(input.length>1) {
          let  zeros='';
          for(let i=0;i<6-input.length;i++)
            {
               zeros = zeros +'0';
            }
            input = input+zeros;
            input = input.substring(0,2)+':'+input.substring(2,4)+':'+input.substring(4);
        }
    }
    return input;
}


export function setErrorOnField(fieldObj: AbstractControl, errKey: string): void {
    const errors = fieldObj.errors;
    if (errors === null) {
        fieldObj.setErrors({ [errKey]: true });
    }
    else {
        errors[errKey] = true;
    }
}

export function clearErrorFromField(fieldObj: AbstractControl, errKey: string): void {
    if ( !!fieldObj.errors && !!fieldObj.errors[errKey] ) {
         delete fieldObj.errors[errKey];
    }

    // if errors is empty object {}, set the errors as null
    if (JSON.stringify(fieldObj.errors) === '{}') {
        fieldObj.setErrors(null);
    }
}

export function getValueOrEmptyString(value:any):any{
    return value ? value : '';
}

export function getColorStyleSuffixForOptionSingleOrder(transactionType: TransactionType): string {
    switch (transactionType) {
        case TransactionType.BUY_CALL_TO_OPEN:
        case TransactionType.BUY_PUT_TO_OPEN:
        case TransactionType.BUY_CALL_TO_CLOSE:
        case TransactionType.BUY_PUT_TO_CLOSE:
            return '_Buy';

        case TransactionType.SELL_CALL_TO_OPEN:
        case TransactionType.SELL_PUT_TO_OPEN:
        case TransactionType.SELL_COVERED_CALLS:
        case TransactionType.CLOSE_EXISTING_CALL:
        case TransactionType.CLOSE_EXISTING_PUT:
            return '_Sell';

        default:
            return '_Default';
    }
}

export function getColorStyleSuffixForOptionMultilegOrder(orderType: OrderType) {
    switch (orderType) {
      case OrderType.NET_CREDIT:
          return '_Sell';
      case OrderType.NET_DEBIT:
          return '_Buy';
      default:
          return '_Default';
    }
}

export function getOrientation() {
    if(screen.availHeight > screen.availWidth){
        return 'portrait';
    } else {
        return 'landscape';
    }
}

// calculating Greatest Common Divisor of 2 intergers
export function calculateGCD(a: number, b: number): number{
    if (b) {
        return calculateGCD(b, a % b);
    } else {
        return Math.abs(a);
    }
}
